import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Star, ShieldCheck, Truck, RefreshCw, Heart, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [rev, setRev] = useState({ rating: 5, comment: "" });
  const { addToCart, toggleWishlist, wishlistIds } = useCart();
  const { user } = useAuth();

  useEffect(() => { setQty(1); api.get(`/products/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return <div className="max-w-7xl mx-auto p-10">Loading...</div>;
  const p = data.product;
  const off = p.mrp && p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const isWished = wishlistIds.has(p.product_id);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please login to write a review"); return; }
    try {
      await api.post("/reviews", { product_id: p.product_id, rating: rev.rating, comment: rev.comment });
      toast.success("Review posted");
      const r = await api.get(`/products/${id}`);
      setData(r.data);
      setRev({ rating: 5, comment: "" });
    } catch { toast.error("Failed to post review"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <nav className="text-sm text-slate-500 mb-8">
        <Link to="/" className="hover:text-blue-900">Home</Link> <span className="mx-2">/</span>
        <Link to={`/products?category=${encodeURIComponent(p.category)}`} className="hover:text-blue-900">{p.category}</Link> <span className="mx-2">/</span>
        <span className="text-slate-900">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="bg-slate-50 rounded-2xl p-8 aspect-square grid place-items-center">
          <img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">{p.brand} · {p.category}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-950">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3 text-sm text-slate-600">
            <div className="flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {p.avg_rating || 4.5}</div>
            <span className="text-slate-300">·</span>
            <span>{p.review_count || 0} reviews</span>
          </div>
          <div className="mt-6 flex items-end gap-3">
            <div className="font-heading text-4xl font-bold text-slate-950">₹{p.price.toLocaleString("en-IN")}</div>
            {p.mrp && <div className="text-slate-400 line-through pb-1">₹{p.mrp.toLocaleString("en-IN")}</div>}
            {off > 0 && <div className="bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-1 rounded-full text-xs">Save {off}%</div>}
          </div>
          <p className="mt-6 text-slate-600 leading-relaxed">{p.description}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-slate-200 rounded-xl">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3" data-testid="pd-qty-minus"><Minus className="w-4 h-4" /></button>
              <div className="px-5 min-w-[3rem] text-center font-medium" data-testid="pd-qty">{qty}</div>
              <button onClick={() => setQty(qty + 1)} className="p-3" data-testid="pd-qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => addToCart(p.product_id, qty)} className="flex-1 bg-blue-900 hover:bg-blue-800 text-white px-6 py-4 rounded-xl font-medium transition" data-testid="pd-add-to-cart">
              Add to Cart
            </button>
            <button onClick={() => toggleWishlist(p.product_id)} className={`p-4 rounded-xl border ${isWished ? "bg-red-50 border-red-200 text-red-500" : "border-slate-200 text-slate-600 hover:text-red-500"}`} data-testid="pd-wishlist">
              <Heart className={`w-5 h-5 ${isWished ? "fill-current" : ""}`} />
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-slate-100">
            {[{i:ShieldCheck,t:"Genuine"},{i:Truck,t:"Free above ₹5K"},{i:RefreshCw,t:"7-day return"}].map((f,i)=>(
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600"><f.i className="w-4 h-4 text-blue-700" /> {f.t}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-16">
        <div className="flex gap-8 border-b border-slate-200 mb-8">
          {[{k:"desc",l:"Description"},{k:"specs",l:"Specifications"},{k:"reviews",l:`Reviews (${data.reviews.length})`}].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`pb-4 font-medium text-sm ${tab === t.k ? "text-blue-900 border-b-2 border-blue-900 -mb-px" : "text-slate-500"}`} data-testid={`pd-tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>
        {tab === "desc" && <div className="prose max-w-3xl text-slate-700">{p.description}</div>}
        {tab === "specs" && (
          <div className="max-w-3xl grid sm:grid-cols-2 gap-3">
            {Object.entries(p.specs || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 py-3">
                <span className="text-slate-500">{k}</span><span className="font-medium text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "reviews" && (
          <div className="max-w-3xl space-y-6">
            <form onSubmit={submitReview} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <div className="font-heading font-semibold mb-3">Write a review</div>
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setRev({...rev, rating: n})} data-testid={`review-star-${n}`}>
                    <Star className={`w-6 h-6 ${n <= rev.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
              <textarea required value={rev.comment} onChange={(e) => setRev({...rev, comment: e.target.value})} rows={3} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm mb-3" placeholder="Share your experience..." data-testid="review-comment"></textarea>
              <button className="bg-blue-900 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium" data-testid="review-submit">Post review</button>
            </form>
            {data.reviews.map((r) => (
              <div key={r.review_id} className="border-b border-slate-100 pb-6">
                <div className="flex justify-between mb-2">
                  <div className="font-medium text-slate-900">{r.user_name}</div>
                  <div className="flex text-amber-400">{Array.from({length:r.rating}).map((_,i)=><Star key={i} className="w-4 h-4 fill-current" />)}</div>
                </div>
                <p className="text-slate-600 text-sm">{r.comment}</p>
              </div>
            ))}
            {data.reviews.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">Be the first to review this product.</div>}
          </div>
        )}
      </div>

      {data.related?.length > 0 && (
        <div className="mt-20">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">Related Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {data.related.map((rp) => <ProductCard key={rp.product_id} product={rp} />)}
          </div>
        </div>
      )}
    </div>
  );
}
